import { ReactElement } from "react";
import type { GetStaticProps, GetStaticPaths } from "next";
import NextHeadSeo from "next-head-seo";

import LayoutMain from "~/components/layouts/LayoutMain";
import { Header } from "~/components/app/Header";
import {
  restApiGetPostBySlugOrFallbackId,
  restApiESGetSettings,
  restApiESQuery,
} from "~/utils/restApi";
import PageMargins from "~/components/styled/PageMargins";
import DisplayAbove from "~/components/styled/DisplayAbove";
import dynamic from "next/dynamic";
import { Heading } from "~/components/ui/Heading";
import { ContentWithSidebar } from "~/components/styled/ContentWithSidebar";
import { FlexibleContent } from "~/components/flexibleContent/FlexibleContent";
import StickySidebar from "~/components/sidebars/StickySidebar";
import SubMenuTwoCol from "~/components/styled/SubMenuTwoCol";
import { SidebarShare } from "~/components/sidebars/SidebarShare";
import { useConfigContext } from "~/providers/ConfigContextProvider";
import { appConfig } from "~/config";
import styled from "styled-components";
import SafeHtmlSpan from "~/components/ui/SafeHtmlSpan";
import { RelatedContent } from "~/components/content/RelatedContent";
import { RelatedProducts } from "~/components/content/RelatedProducts";
import { ErrorLock } from "~/components/app/ErrorLock";
import { ApiImage } from "~/components/ui/ApiImage";
import theme, { themeImgSizes } from "~/theme";

const imgSizes = {
  screen: theme.spacePx("screen", 3),
  desktop: theme.spacePx("desktop", 3),
  tablet: theme.spacePx("tablet", 3),
  mobile: theme.spacePx("mobile", 3),
  base: theme.spacePx("base", 3),
};

const AudioPlayer = dynamic(() => import("~/components/ui/Player/AudioPlayer"));

const AudioContainer = styled.div<{ hasImg?: boolean }>`
  background-color: var(--ikon-bg-color, #fff);
  position: relative;
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        height: ${props.theme.spacePx(breakpoint, 4)};
        margin: 0 ${props.theme.marginPx(breakpoint)};
        margin-top: ${props.hasImg ? "0" : props.theme.spacePx(breakpoint, 8)}
        `;
    })}
`;

const AudioImageContainer = styled.div`
  box-sizing: content-box;

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        height: ${props.theme.spacePx(breakpoint, 3)};
        width: ${props.theme.spacePx(breakpoint, 3)};
        padding: 0 ${props.theme.marginPx(breakpoint)};
        margin: ${props.theme.spacePx(breakpoint, 6)} auto;
        `;
    })}
`;

const AudioDetail = ({ data }: { data: any }) => {
  const config = useConfigContext();

  const type = data?.taxonomies?.media_category?.[0]?.name ?? "Audio";

  return (
    <>
      <NextHeadSeo
        canonical={data?.yoast_head_json?.canonical}
        title={
          data?.yoast_head_json?.title ??
          `${data?.title} - Audio - Media - ${appConfig.appTitle}`
        }
        description={data?.yoast_head_json?.description}
        og={{
          title: data?.yoast_head_json?.og_title,
          type: data?.yoast_head_json?.og_type,
          siteName: data?.yoast_head_json?.og_site_name,
          image: data?.yoast_head_json?.twitter_image,
        }}
        twitter={{
          card: "summary_large_image",
        }}
      />
      <Header>
        <SubMenuTwoCol width="auto" alignSelf="center" colGapSpace={8}>
          <Heading space={7} heading="h3" inline trimmed html={data?.title} />

          <DisplayAbove breakpoint="tabletLandscape" inline>
            <Heading space={0} heading="h4" inline>
              <SafeHtmlSpan html={type} />
              &emsp;
              <span style={{ fontWeight: 400 }}>
                <SafeHtmlSpan html={data.acf.duration} />
              </span>
            </Heading>
          </DisplayAbove>
        </SubMenuTwoCol>
      </Header>

      <main id="content">
        {data?.acf?.mp3?.url &&
          data?.acf?.mp3?.mime_type?.indexOf("audio") > -1 && (
            <ErrorLock>
              {data?.featuredImage?.sizes && (
                <AudioImageContainer>
                  <ApiImage
                    alt=""
                    ratio="1 / 1"
                    imgSizes={themeImgSizes(imgSizes)}
                    sizes={data?.featuredImage?.sizes}
                    mode="fill"
                  />
                </AudioImageContainer>
              )}
              <AudioContainer hasImg={!!data?.featuredImage?.sizes}>
                <AudioPlayer
                  src={data?.acf?.mp3?.url}
                  title={`<b>AUDIO</b>&emsp;${data?.title}`}
                />
              </AudioContainer>
            </ErrorLock>
          )}

        <PageMargins spaceTop={9} spaceBottom={5}>
          <Heading space={7} heading="h4">
            <SafeHtmlSpan html={type} />
            &emsp;
            <span style={{ fontWeight: 400 }}>
              <SafeHtmlSpan html={data.acf.duration} />
            </span>
          </Heading>

          <Heading
            asTag
            heading="h1"
            space={data?.acf?.sub_title?.trim() ? 8 : 6}
            html={data?.title}
          />

          {data?.acf?.sub_title?.trim() && (
            <Heading
              heading="h2"
              space={6}
              html={data?.acf?.sub_title?.trim()}
            />
          )}

          <ContentWithSidebar>
            {data?.acf?.content?.length > 0 ? (
              <FlexibleContent
                columnWidth="full"
                field="content"
                content={data.acf.content}
              />
            ) : (
              <div />
            )}
            <StickySidebar>
              <SidebarShare
                title={data?.title}
                url={`${config.baseUrl}/media/audio/${data?.slug}`}
              />
            </StickySidebar>
          </ContentWithSidebar>
        </PageMargins>
        <RelatedProducts
          key={`audio-products-${data?.id}`}
          id={data?.id}
          ids={data?.acf?.shop_items ?? []}
        />
        <RelatedContent
          key={`audio-related-${data?.id}`}
          ids={[data?.id]}
          relatedItems={data?.acf?.related_content ?? []}
        />
      </main>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const posts = await restApiESQuery({
    type: "audio_file",
    perPage: 100,
    orderby: "post_date",
    order: "desc",
  });

  return {
    paths: posts?.data?.length ? posts?.data.map((item: any) => item.url) : [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const token = (context?.previewData as any)?.token;

  const data = await restApiGetPostBySlugOrFallbackId(
    "audio",
    context?.params?.slug,
    token
  );
  const frontendSettings = await restApiESGetSettings(["mediaCategory"]);
  if (!data)
    return {
      props: {
        frontendSettings,
      },
      notFound: true,
      revalidate: frontendSettings?.options?.copyrightNotice ? 240 : 15,
    };

  return {
    props: {
      frontendSettings,
      data,
    },
    revalidate: frontendSettings?.options?.copyrightNotice
      ? appConfig.revalidateInterval("audioDetail", {
          date: data?.date,
          modified: data?.modified,
        })
      : 15,
  };
};

AudioDetail.getLayout = function getLayout(page: ReactElement, props: any) {
  return <LayoutMain props={props}>{page}</LayoutMain>;
};
export default AudioDetail;
